// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionableNotification,
  Button,
  InlineNotification,
  TextArea,
  TextInput,
} from '@carbon/react';
import {
  describeApiError,
  useOrganization,
  useRegions,
  useProvisionSite,
} from '../../shared/provisioning';
import type { ProvisionSiteRequest } from '../../shared/provisioning';
import form from './forms.module.css';
import styles from './NewSitePage.module.css';

// Intersection of the OMS DomainKey API pattern (letter-led, alphanumeric,
// single separators) and the oms.Sites CHK constraint (lowercase, digits,
// hyphens, 3–50): lowercase letter-led, digits, single hyphen separators.
const SITE_KEY = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

export default function NewSitePage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const {
    data: org,
    isLoading: orgLoading,
    isError: orgError,
    error: orgErrorValue,
  } = useOrganization(orgId);
  const {
    data: regions,
    isLoading: regionsLoading,
    isError: regionsError,
    refetch: refetchRegions,
  } = useRegions(orgId);
  const provision = useProvisionSite(orgId);

  const [siteKey, setSiteKey] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Regions carry no user-facing functionality yet, so they stay hidden: every
  // org is expected to have exactly one region (created with the org), and site
  // creation uses it implicitly (agreed with the dc-ops owner, 2026-09-08).
  const regionId = regions?.[0]?.regionId;

  const errors: Record<string, string> = {};
  if (!siteKey) errors.siteKey = 'Required';
  else if (siteKey.length < 3 || siteKey.length > 50)
    errors.siteKey = 'Between 3 and 50 characters';
  else if (!SITE_KEY.test(siteKey))
    errors.siteKey = 'Lowercase letters and digits with single hyphens, starting with a letter';
  if (!label.trim()) errors.label = 'Required';

  const ready = !!org && !!regionId && !orgLoading && !regionsLoading;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length > 0 || !orgId || !ready) return;

    // Identifier/label split per the seed-KG mapping (dc-ops owner,
    // 2026-09-08): the *_name fields are identifiers (-> wattsch:upstreamId
    // and the URN slug), the description fields are display labels
    // (-> rdfs:label). legalEntityName is not used in the site KG.
    const body: ProvisionSiteRequest = {
      organizationId: orgId,
      organizationName: org.organizationKey,
      organizationDescription: org.preferredName,
      siteKey,
      regionId,
      label: label.trim(),
      siteName: siteKey,
      siteDescription: description.trim() || label.trim(),
    };
    provision.mutate(body, { onSuccess: run => navigate(`/runs/${run.runId}`) });
  };

  return (
    <>
      <div className={styles.head}>
        <h1 className={styles.title}>New site</h1>
        {org && <span className={styles.org}>{org.preferredName}</span>}
      </div>

      {provision.isError && (
        <div className={form.notice}>
          <InlineNotification
            kind="error"
            lowContrast
            title={describeApiError(provision.error, { action: 'create a site' }).title}
            subtitle={describeApiError(provision.error, { action: 'create a site' }).message}
            hideCloseButton
          />
        </div>
      )}

      {orgError && (
        <div className={form.notice}>
          <InlineNotification
            kind="error"
            lowContrast
            title="Could not load the organization"
            subtitle={describeApiError(orgErrorValue, { action: 'view this organization' }).message}
            hideCloseButton
          />
        </div>
      )}

      {regionsError && (
        <div className={form.notice}>
          <ActionableNotification
            kind="error"
            lowContrast
            title="Could not load the organization's region"
            subtitle="Site creation needs it. Retry, and escalate if this keeps failing."
            actionButtonLabel="Retry"
            onActionButtonClick={() => refetchRegions()}
            hideCloseButton
          />
        </div>
      )}

      {!regionsLoading && !regionsError && !regionId && (
        <div className={form.notice}>
          <InlineNotification
            kind="warning"
            lowContrast
            title="Organization not ready"
            subtitle="This organization has no region to place sites in yet. Region setup is part of organization creation — contact the platform team."
            hideCloseButton
          />
        </div>
      )}

      <form className={form.form} onSubmit={onSubmit} noValidate data-testid="new-site-form">
        <div className={form.row}>
          <TextInput
            id="siteKey"
            labelText="Site key"
            value={siteKey}
            onChange={e => setSiteKey(e.target.value)}
            invalid={submitted && !!errors.siteKey}
            invalidText={submitted ? errors.siteKey : undefined}
          />
          <TextInput
            id="label"
            labelText="Label"
            value={label}
            onChange={e => setLabel(e.target.value)}
            invalid={submitted && !!errors.label}
            invalidText={submitted ? errors.label : undefined}
          />
        </div>

        <div className={form.field}>
          <TextArea
            id="siteDescription"
            labelText="Description"
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        <div className={form.actions}>
          <Button type="submit" disabled={provision.isPending || !ready}>
            {provision.isPending ? 'Starting…' : 'Create site'}
          </Button>
          <Button kind="ghost" onClick={() => navigate(`/organizations/${orgId}/sites`)}>
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
}
